/**
 * Monta os grupos da paleta (I1) a partir do que JÁ existe: o menu (`buildNavigation`, que
 * filtra por papel) e as listas de campanhas, grupos e números. Esconder por papel é UX;
 * quem barra é o servidor.
 */
import { createElement, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  Keyboard,
  LayoutGrid,
  Megaphone,
  MessageSquarePlus,
  Plus,
  Radio,
  Sparkles,
  Users,
} from 'lucide-react';
import { CommandPalette } from '@mgdlead/ui';
import type { CommandGroup, CommandItem } from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import type { WorkspaceRole } from '@mgdlead/shared';

import { buildNavigation, workspacePath } from '../workspace/navigation';
import { usePaletteSources } from './experience-api';
import { useExperience } from './ExperienceProvider';
import { GO_SHORTCUTS } from './shortcuts';

const SHORTCUT_BY_NAV = new Map(
  GO_SHORTCUTS.map((s) => [s.action.kind === 'go' ? s.action.navId : '', s.keys] as const),
);

export interface CommandPaletteHostProps {
  workspaceId: string;
  slug: string;
  role: WorkspaceRole;
}

export function CommandPaletteHost({ workspaceId, slug, role }: CommandPaletteHostProps) {
  const navigate = useNavigate();
  const { dialog, closeDialog, openDialog, density, setDensity } = useExperience();
  const open = dialog === 'palette';

  const { campaigns, groups, monitors } = usePaletteSources(workspaceId, {
    campaigns: open && can(role, 'campaign:read'),
    groups: open && can(role, 'group:read'),
    monitors: open && can(role, 'monitor:read'),
  });

  const paletteGroups = useMemo<CommandGroup[]>(() => {
    const go = (path: string) => () => void navigate(workspacePath(slug, path));

    const goTo: CommandItem[] = buildNavigation(slug, role).flatMap((group) =>
      group.items.map((item) => {
        const keys = SHORTCUT_BY_NAV.get(item.id);
        return {
          id: item.id,
          label: item.label,
          ...(group.label ? { description: group.label } : {}),
          icon: createElement(item.icon),
          ...(keys ? { shortcut: keys } : {}),
          onSelect: () => void navigate(item.href),
        };
      }),
    );
    goTo.push({
      id: 'novidades',
      label: 'Novidades',
      icon: <Sparkles />,
      onSelect: go('novidades'),
    });

    const result: CommandGroup[] = [{ id: 'ir', label: 'Ir para', items: goTo }];

    if (can(role, 'campaign:read')) {
      result.push({
        id: 'campanhas',
        label: 'Campanhas',
        loading: campaigns.isPending,
        ...(campaigns.isError ? { error: 'Não deu para carregar as campanhas agora.' } : {}),
        items: (campaigns.data?.campaigns ?? []).map((c) => ({
          id: c.id,
          label: c.name,
          description: `Código ${c.code}`,
          keywords: [c.code],
          icon: <Megaphone />,
          onSelect: go(`campanhas/${c.id}`),
        })),
      });
    }
    if (can(role, 'group:read')) {
      result.push({
        id: 'grupos',
        label: 'Grupos',
        loading: groups.isPending,
        ...(groups.isError ? { error: 'Não deu para carregar os grupos agora.' } : {}),
        items: (groups.data?.groups ?? []).map((g) => ({
          id: g.id,
          label: g.name ?? 'Grupo sem nome',
          description: g.monitorName,
          icon: <Users />,
          onSelect: go(`grupos/${g.id}`),
        })),
      });
    }
    if (can(role, 'monitor:read')) {
      result.push({
        id: 'monitores',
        label: 'Monitores',
        loading: monitors.isPending,
        ...(monitors.isError ? { error: 'Não deu para carregar os números agora.' } : {}),
        items: (monitors.data?.monitors ?? []).map((m) => ({
          id: m.id,
          label: m.name,
          description: m.status === 'CONNECTED' ? 'Conectado' : 'Desconectado',
          keywords: ['número', 'whatsapp', 'monitorador'],
          icon: <Radio />,
          onSelect: go('monitoradores'),
        })),
      });
    }

    const actions: CommandItem[] = [];
    if (can(role, 'redirector:create')) {
      actions.push({
        id: 'novo-redirecionador',
        label: 'Novo redirecionador',
        icon: <Plus />,
        keywords: ['link'],
        onSelect: go('redirecionadores?novo=1'),
      });
    }
    if (can(role, 'campaign:create')) {
      actions.push({
        id: 'nova-campanha',
        label: 'Nova campanha',
        icon: <Plus />,
        onSelect: go('campanhas?novo=1'),
      });
    }
    actions.push(
      {
        id: 'densidade',
        label: density === 'compact' ? 'Usar layout confortável' : 'Usar layout compacto',
        icon: <LayoutGrid />,
        keywords: ['densidade', 'layout'],
        onSelect: () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
      },
      {
        id: 'sugestao',
        label: 'Enviar sugestão',
        icon: <MessageSquarePlus />,
        keywords: ['feedback', 'nps'],
        onSelect: () => openDialog('feedback'),
      },
      {
        id: 'atalhos',
        label: 'Ver atalhos do teclado',
        icon: <Keyboard />,
        shortcut: ['?'],
        onSelect: () => openDialog('shortcuts'),
      },
    );
    result.push({ id: 'acoes', label: 'Ações', items: actions });
    return result;
  }, [slug, role, navigate, campaigns, groups, monitors, density, setDensity, openDialog]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={(next) => (next ? openDialog('palette') : closeDialog())}
      groups={paletteGroups}
    />
  );
}
